'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { PDFDocument, degrees } from 'pdf-lib';
import { createCleanorExportFileName, formatFileSize, isPdfFile } from '@/lib/browser-pdf-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  readPdfPagePreviews,
  revokePdfPagePreviews,
  revokePdfSource,
  type PdfToolPagePreview,
  type PdfToolSource,
} from '@/components/tools/pdf-tool-shared';

export type PdfPageActionVariant = 'rotate-pdf-pages' | 'delete-pdf-pages' | 'extract-pdf-pages';

type PageActionPage = PdfToolPagePreview & {
  rotation: 0 | 90 | 180 | 270;
};

const toolCopy: Record<
  PdfPageActionVariant,
  {
    title: string;
    subtitle: string;
    badge: string;
    buttonLabel: string;
    processingLabel: string;
    successLabel: string;
  }
> = {
  'rotate-pdf-pages': {
    title: 'Rotate selected PDF pages',
    subtitle: 'Load one PDF, rotate individual pages or the whole set, and export an updated copy.',
    badge: 'Rotate',
    buttonLabel: 'Export rotated PDF',
    processingLabel: 'Rotating…',
    successLabel: 'PDF rotation',
  },
  'delete-pdf-pages': {
    title: 'Delete pages from one PDF',
    subtitle: 'Mark the pages you do not need and export one cleaned-up PDF locally.',
    badge: 'Delete',
    buttonLabel: 'Export cleaned PDF',
    processingLabel: 'Deleting pages…',
    successLabel: 'PDF page delete',
  },
  'extract-pdf-pages': {
    title: 'Extract selected PDF pages',
    subtitle: 'Pick the pages you want to keep and export them into one new PDF file.',
    badge: 'Extract',
    buttonLabel: 'Export selected pages',
    processingLabel: 'Extracting pages…',
    successLabel: 'PDF page extract',
  },
};

function nextRotation(current: 0 | 90 | 180 | 270, delta: -90 | 90) {
  return ((current + delta + 360) % 360) as 0 | 90 | 180 | 270;
}

export default function BrowserPdfPageActionTool({ variant }: { variant: PdfPageActionVariant }) {
  const copy = toolCopy[variant];
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [source, setSource] = useState<PdfToolSource | null>(null);
  const [pages, setPages] = useState<PageActionPage[]>([]);
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);

  const selectedCount = useMemo(() => {
    if (variant === 'delete-pdf-pages') {
      return selectedPages.length;
    }

    if (variant === 'extract-pdf-pages') {
      return selectedPages.length;
    }

    return pages.filter((page) => page.rotation !== 0).length;
  }, [pages, selectedPages, variant]);

  useEffect(
    () => () => {
      revokePdfSource(source);
      revokePdfPagePreviews(pages);
    },
    [pages, source],
  );

  function replaceLoaded(nextSource: PdfToolSource | null, nextPages: PageActionPage[]) {
    setSource((current) => {
      revokePdfSource(current);
      return nextSource;
    });
    setPages((current) => {
      revokePdfPagePreviews(current);
      return nextPages;
    });
  }

  async function handleFileSelection(file: File | null) {
    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);
    setSelectedPages([]);

    if (!file) {
      replaceLoaded(null, []);
      return;
    }

    if (!isPdfFile(file)) {
      replaceLoaded(null, []);
      setErrorMessage('Choose one PDF file.');
      return;
    }

    setIsLoading(true);
    try {
      const loaded = await readPdfPagePreviews(file);
      replaceLoaded(
        loaded.source,
        loaded.pages.map((page) => ({
          ...page,
          rotation: 0,
        })),
      );
    } catch (error) {
      replaceLoaded(null, []);
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be opened.');
    } finally {
      setIsLoading(false);
    }
  }

  function clearSource() {
    replaceLoaded(null, []);
    setSelectedPages([]);
    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  function togglePage(pageNumber: number) {
    setSelectedPages((current) =>
      current.includes(pageNumber)
        ? current.filter((value) => value !== pageNumber)
        : [...current, pageNumber].sort((left, right) => left - right),
    );
  }

  function rotatePage(pageNumber: number, delta: -90 | 90) {
    setPages((current) =>
      current.map((page) =>
        page.pageNumber === pageNumber
          ? { ...page, rotation: nextRotation(page.rotation, delta) }
          : page,
      ),
    );
  }

  function rotateAll(delta: -90 | 90) {
    setPages((current) =>
      current.map((page) => ({ ...page, rotation: nextRotation(page.rotation, delta) })),
    );
  }

  async function runAction() {
    if (!source) {
      setErrorMessage('Choose a PDF first.');
      return;
    }

    if (variant !== 'rotate-pdf-pages' && !selectedPages.length) {
      setErrorMessage(
        variant === 'delete-pdf-pages'
          ? 'Select at least one page to remove.'
          : 'Select at least one page to extract.',
      );
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another PDF page export right now.',
      );

      const bytes = await source.file.arrayBuffer();
      const document = await PDFDocument.load(bytes);

      let output: Blob;
      if (variant === 'rotate-pdf-pages') {
        const orderedPages = document.getPages();
        pages.forEach((page, index) => {
          if (page.rotation) {
            orderedPages[index]?.setRotation(degrees(page.rotation));
          }
        });
        output = new Blob([(await document.save()) as unknown as BlobPart], {
          type: 'application/pdf',
        });
      } else if (variant === 'delete-pdf-pages') {
        const deleteIndexes = selectedPages
          .map((pageNumber) => pageNumber - 1)
          .sort((a, b) => b - a);
        deleteIndexes.forEach((index) => document.removePage(index));
        output = new Blob([(await document.save()) as unknown as BlobPart], {
          type: 'application/pdf',
        });
      } else {
        const nextDocument = await PDFDocument.create();
        const copied = await nextDocument.copyPages(
          document,
          selectedPages.map((pageNumber) => pageNumber - 1),
        );
        copied.forEach((page) => nextDocument.addPage(page));
        output = new Blob([(await nextDocument.save()) as unknown as BlobPart], {
          type: 'application/pdf',
        });
      }

      const url = URL.createObjectURL(output);
      downloadBlob(
        url,
        createCleanorExportFileName(
          variant,
          [source.name.replace(/\.pdf$/i, ''), selectedCount || source.pageCount],
          'pdf',
        ),
      );
      setTimeout(() => URL.revokeObjectURL(url), 0);
      setResultSize(output.size);
      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The PDF pages could not be exported.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <article className="project-detail-card tool-live-card">
      <div className="tool-live-heading">
        <div>
          <h2>{copy.title}</h2>
          <p>{copy.subtitle}</p>
        </div>
        <span className="tool-live-badge">{copy.badge}</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.name : 'Choose one PDF or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.size)} • ${source.pageCount} pages`
                  : 'Load one PDF to preview and edit the page stack'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          {variant === 'rotate-pdf-pages' && pages.length ? (
            <div className="hero-actions tool-live-actions">
              <button className="button button-ghost" onClick={() => rotateAll(-90)} type="button">
                Rotate all left
              </button>
              <button className="button button-ghost" onClick={() => rotateAll(90)} type="button">
                Rotate all right
              </button>
            </div>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isLoading || isProcessing}
              onClick={() => void runAction()}
              type="button"
            >
              {isProcessing ? copy.processingLabel : copy.buttonLabel}
            </button>
            {source ? (
              <button className="button button-ghost" onClick={clearSource} type="button">
                Clear
              </button>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Action summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Pages</strong>
              <span>{source?.pageCount ?? '—'}</span>
            </div>
            <div>
              <strong>Selected</strong>
              <span>{selectedCount || 'Nothing selected yet'}</span>
            </div>
            <div>
              <strong>Source size</strong>
              <span>{source ? formatFileSize(source.size) : '—'}</span>
            </div>
            <div>
              <strong>Result</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
          </div>
        </aside>
      </div>

      {pages.length ? (
        <div className="tool-live-preview">
          <h3>Page preview</h3>
          <div className="tool-live-results-grid">
            {pages.map((page) => {
              const isSelected = selectedPages.includes(page.pageNumber);
              return (
                <article className="tool-result-card" key={page.id}>
                  <img
                    alt={`Page ${page.pageNumber}`}
                    className="tool-result-image"
                    src={page.previewUrl}
                    style={{
                      transform:
                        variant === 'rotate-pdf-pages' && page.rotation
                          ? `rotate(${page.rotation}deg)`
                          : undefined,
                    }}
                  />
                  <strong>{`Page ${page.pageNumber}`}</strong>
                  {variant === 'rotate-pdf-pages' ? (
                    <div className="hero-actions tool-live-actions">
                      <button
                        className="button button-ghost"
                        onClick={() => rotatePage(page.pageNumber, -90)}
                        type="button"
                      >
                        Left
                      </button>
                      <button
                        className="button button-ghost"
                        onClick={() => rotatePage(page.pageNumber, 90)}
                        type="button"
                      >
                        Right
                      </button>
                    </div>
                  ) : (
                    <label className="tool-inline-choice">
                      <input
                        checked={isSelected}
                        onChange={() => togglePage(page.pageNumber)}
                        type="checkbox"
                      />
                      <span>
                        {variant === 'delete-pdf-pages' ? 'Remove on export' : 'Include in export'}
                      </span>
                    </label>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      ) : null}
    </article>
  );
}

function downloadBlob(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}
